import React, { useMemo } from "react";
import {
  Button,
  DatePicker,
  Header,
  Multiselect,
  Pagination,
  Popover,
  Select,
  Table,
} from "@amzn/awsui-components-react";
import { OptionDefinition } from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces";
import { useRecentTransactions } from "../hooks/useRecentTransactions";
import {
  COLUMN_DEFINITIONS, CONSUMPTION_TYPE_OPTIONS,
  DEFAULT_OPTION,
  getModifiedOptions,
  GLS,
  GROUPED_MARKETPLACES,
  GROUPED_USECASES,
  PAYMENT_METHOD_OPTIONS,
  RECENT_TRXN_EMPTY_STATE,
  STAGES,
  TABLE_PREFERENCES,
  TIME_RANGES,
} from "../config/recentTransactionTableConfig";
import ViewLogs from "./common/ViewLogs";
import { getUslPublisherLogGroups } from "../helpers/logUtils";
import { EmptyState } from "./common/EmptyState";
import {Transaction} from "../lusca-search-api/generated-src";
import { SearchFilters } from "../types/common";

interface RecentTransactionsProps {
  isRaven?: boolean;
  selectedOrders?: Transaction[];
  setSelectedOrders?: (orders: Transaction[]) => void;
}

export default function RecentTransactions(props : RecentTransactionsProps) {
  const {
    items,
    collectionProps,
    filterProps,
    paginationProps,
    handleSearch,
    isLoading,
    selectedFilters,
    setSelectedFilters,
    clearFilter,
    executedQuery,
    error,
  } = useRecentTransactions();

  const modifiedMarketplaces = useMemo(
    () => getModifiedOptions(GROUPED_MARKETPLACES, selectedFilters.marketplaces),
    [selectedFilters.marketplaces]
  );

  const modifiedUsecases = useMemo(
    () => getModifiedOptions(GROUPED_USECASES, selectedFilters.usecases),
    [selectedFilters.usecases]
  );

  const handleMultiselectChange =
    <K extends keyof SearchFilters>(filter: K) =>
    ({
      detail,
    }: {
      detail: { selectedOptions: readonly OptionDefinition[] };
    }) => {
      let newSelection = [...detail.selectedOptions] as OptionDefinition[];
      const currentSelection = selectedFilters[filter] as OptionDefinition[]
      const hadAll = currentSelection.some(
        (option) => option.value === DEFAULT_OPTION.value
      );
      const hasAll = newSelection.some(
        (option) => option.value === DEFAULT_OPTION.value
      );

      if (hasAll && !hadAll) {
        newSelection = [DEFAULT_OPTION];
      } else if (hadAll) {
        newSelection = [];
      }
      setSelectedFilters({
        ...selectedFilters,
        [filter]: newSelection,
      });
    };

  return (
    <Table
      {...collectionProps}
      {...TABLE_PREFERENCES}
      resizableColumns
      columnDefinitions={COLUMN_DEFINITIONS}
      items={items}
      loading={isLoading}
      loadingText="Loading transactions"
      selectionType={props.isRaven ? "multi" : undefined}
      onSelectionChange={({ detail }) => {
        if (props.setSelectedOrders) props.setSelectedOrders(detail.selectedItems);
      }}
      selectedItems={props.selectedOrders}
      header={
        <Header variant="awsui-h1-sticky">
          Find Transactions{" "}
          <Popover
            dismissButton={false}
            fixedWidth={false}
            position="bottom"
            triggerType="custom"
            content="Searches are limited to 5 minute intervals from the time range specified. To choose a specific time range, or add custom filters, open in CloudWatch"
          >
            <Button iconName="status-info" variant="inline-icon" />
          </Popover>
        </Header>
      }
      filter={
        <div className="input-container">
          <div className="select-filter" style={{ maxInlineSize: "110px" }}>
            <Select
              data-testid="stage-filter"
              inlineLabelText="Stage"
              options={STAGES}
              selectedAriaLabel="Selected"
              selectedOption={selectedFilters.stage}
              onChange={({ detail }) =>
                setSelectedFilters({
                  ...selectedFilters,
                  stage: detail.selectedOption,
                })
              }
              expandToViewport
            />
          </div>
          <div className="select-filter" style={{ maxInlineSize: "150px" }}>
            <Select
              inlineLabelText="GL"
              data-testid="gl-filter"
              options={GLS}
              selectedAriaLabel="Selected"
              selectedOption={selectedFilters.gl}
              onChange={({ detail }) =>
                setSelectedFilters({
                  ...selectedFilters,
                  gl: detail.selectedOption,
                })
              }
              expandToViewport
              filteringType="auto"
              filteringPlaceholder="Find GL"
              empty="No matching GLs"
            />
          </div>
          <div className="select-filter" style={{ maxInlineSize: "150px" }}>
            <Select
              inlineLabelText="Payment Type"
              data-testid="payment-method-filter"
              options={PAYMENT_METHOD_OPTIONS}
              selectedAriaLabel="Selected"
              selectedOption={selectedFilters.paymentMethod}
              onChange={({ detail }) =>
                setSelectedFilters({
                  ...selectedFilters,
                  paymentMethod: detail.selectedOption,
                })
              }
              expandToViewport
            />
          </div>
          <div className="select-filter">
            <Multiselect
              placeholder="Marketplaces"
              inlineTokens
              data-testid="marketplace-filter"
              options={modifiedMarketplaces}
              selectedOptions={selectedFilters.marketplaces}
              onChange={handleMultiselectChange("marketplaces")}
              expandToViewport
              filteringType="auto"
              filteringPlaceholder="Find marketplaces"
              empty="No matching marketplaces"
            />
          </div>
          <div className="select-filter">
            <Multiselect
              placeholder="Usecases"
              data-testid="usecase-filter"
              inlineTokens
              options={modifiedUsecases}
              selectedOptions={selectedFilters.usecases}
              onChange={handleMultiselectChange("usecases")}
              filteringType="auto"
              expandToViewport
              filteringPlaceholder="Find usecases"
              empty="No matching usecases"
            />
          </div>
          <div className="select-filter">
            <Multiselect
              placeholder="Consumption Type"
              data-testid="consumption-type-filter"
              inlineTokens
              options={CONSUMPTION_TYPE_OPTIONS}
              selectedOptions={selectedFilters.consumptionTypes}
              onChange={handleMultiselectChange("consumptionTypes")}
              filteringType="auto"
              expandToViewport
              filteringPlaceholder="Find Consumption Type"
              empty="No matching consumption type"
            />
          </div>
          <div className="select-filter" style={{ maxInlineSize: "150px" }}>
            <Select
              inlineLabelText="Time Range"
              data-testid="time-range-filter"
              options={TIME_RANGES}
              selectedAriaLabel="Selected"
              selectedOption={selectedFilters.timeRange}
              onChange={({ detail }) => {
                setSelectedFilters({
                  ...selectedFilters,
                  timeRange: detail.selectedOption,
                  date:
                    detail.selectedOption.value !== "custom"
                      ? ""
                      : selectedFilters.date,
                });
              }}
              expandToViewport
            />
          </div>
          {selectedFilters.timeRange.value === "custom" && (
            <div className="select-filter" style={{ maxInlineSize: "200px" }}>
              <DatePicker
                onChange={({ detail }) =>
                  setSelectedFilters({
                    ...selectedFilters,
                    date: detail.value
                  })
                }
                value={selectedFilters.date}
                placeholder="Select a date"
                previousMonthAriaLabel="Previous month"
                nextMonthAriaLabel="Next month"
                todayAriaLabel="Today"
                expandToViewport
                isDateEnabled={(date) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return new Date(date) <= today;
                }}
              />
            </div>
          )}

          <Button variant="primary" onClick={handleSearch}>
            Search
          </Button>
          <Button onClick={clearFilter}>Clear filters</Button>
          <ViewLogs
            stage={selectedFilters.stage.value!}
            logGroups={getUslPublisherLogGroups(selectedFilters.stage.value!)}
            logQuery={executedQuery!}
            minimal
          />
        </div>
      }
      pagination={<Pagination {...paginationProps} />}
      empty={
        <EmptyState
          error={error}
          filteringText={filterProps.filteringText}
          config={RECENT_TRXN_EMPTY_STATE}
        />
      }
    />
  );
}
